import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box, TextField, List, ListItem, ListItemButton, ListItemText, ListItemAvatar,
  Avatar, Checkbox, Chip, Typography, CircularProgress, Paper, InputAdornment,
  Pagination, Accordion, AccordionSummary, AccordionDetails, FormControl,
  InputLabel, Select, MenuItem, Slider, FormControlLabel, Button, Stack
} from "@mui/material";
import { Search, Person, ExpandMore, FilterList } from "@mui/icons-material";
import axios from "../AxiosUrl";

const debounce = (fn, wait) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), wait); }; };

const defaultFilters = {
  gender: "all", ageRange: { min: 18, max: 70 }, subscriptionStatus: [],
  lastActiveWithin: null, profileCompleted: false, hasPhoto: false, hasCV: false,
  hasExpoToken: false, hasMobile: false, skipInactive: false, skipDisabled: false, skipWithoutCV: false
};

const UserSelector = ({ selectedUserIds = [], onUserSelection, onRemoveUser }) => {
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState(defaultFilters);
  const [showFilters, setShowFilters] = useState(false);
  const listRef = useRef(null);

  // Fetch selected user details
  useEffect(() => {
    if (selectedUserIds.length === 0) { setSelectedUsers([]); return; }
    axios.post("/users/batch", { userIds: selectedUserIds })
      .then(r => r.data.success && setSelectedUsers(r.data.data))
      .catch(() => {});
  }, [selectedUserIds]);

  // Load users
  const loadUsers = useCallback(async (pg = 1, q = "", f = filters) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: 20 });
      if (q?.length >= 2) params.append("query", q.trim());

      const body = {};
      if (f.gender !== "all") body.gender = f.gender;
      if (f.ageRange.min !== 18 || f.ageRange.max !== 70) body.ageRange = f.ageRange;
      if (f.subscriptionStatus?.length) body.subscriptionStatus = f.subscriptionStatus;
      if (f.lastActiveWithin) body.lastActiveWithin = f.lastActiveWithin;
      ["profileCompleted", "hasPhoto", "hasCV", "hasExpoToken", "hasMobile", "skipInactive", "skipDisabled", "skipWithoutCV"]
        .forEach(k => { if (f[k]) body[k] = true; });

      const res = await axios.post(`/users/list?${params}`, body);
      if (res.data.success) {
        setUsers(res.data.data || []);
        setPagination(res.data.pagination || { page: 1, total: 0, totalPages: 0 });
      }
    } catch (e) {
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const debouncedSearch = useRef(debounce(q => { setPage(1); loadUsers(1, q, filters); }, 500)).current;

  useEffect(() => { loadUsers(1, "", filters); }, []);
  useEffect(() => { loadUsers(page, search, filters); }, [page]);
  useEffect(() => { search.length >= 2 ? debouncedSearch(search) : search.length === 0 && loadUsers(1, "", filters); }, [search]);

  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const applyFilters = () => { setPage(1); loadUsers(1, search, filters); };
  const clearFilters = () => { setFilters(defaultFilters); setPage(1); loadUsers(1, search, defaultFilters); };
  const toggle = (user) => onUserSelection?.(user.id, !selectedUserIds.includes(user.id));
  const handlePage = (e, v) => { setPage(v); listRef.current?.scrollTo(0, 0); };

  return (
    <Box>
      <TextField fullWidth label="Search Users" placeholder="Search by name, mobile, email..." value={search}
        onChange={e => setSearch(e.target.value)} margin="normal" helperText="Type 2+ characters to search"
        InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />

      {/* Filters Toggle */}
      <Button variant={showFilters ? "contained" : "outlined"} startIcon={<FilterList />} fullWidth sx={{ my: 1 }} onClick={() => setShowFilters(!showFilters)}>
        {showFilters ? "Hide Filters" : "Show Filters"}
      </Button>

      {showFilters && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="subtitle2">Filter Users</Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" onClick={clearFilters}>Clear</Button>
              <Button size="small" variant="contained" onClick={applyFilters}>Apply</Button>
            </Stack>
          </Box>

          <Accordion><AccordionSummary expandIcon={<ExpandMore />}><Typography>Demographics</Typography></AccordionSummary>
            <AccordionDetails>
              <FormControl fullWidth size="small" margin="dense"><InputLabel>Gender</InputLabel>
                <Select value={filters.gender} label="Gender" onChange={e => setFilter("gender", e.target.value)}>
                  <MenuItem value="all">All</MenuItem><MenuItem value="male">Male</MenuItem><MenuItem value="female">Female</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="body2" sx={{ mt: 1 }}>Age: {filters.ageRange.min} - {filters.ageRange.max}</Typography>
              <Slider value={[filters.ageRange.min, filters.ageRange.max]} min={18} max={70} valueLabelDisplay="auto"
                onChange={(e, v) => setFilter("ageRange", { min: v[0], max: v[1] })} />
            </AccordionDetails>
          </Accordion>

          <Accordion><AccordionSummary expandIcon={<ExpandMore />}><Typography>Subscription</Typography></AccordionSummary>
            <AccordionDetails>
              <FormControl fullWidth size="small"><InputLabel>Status</InputLabel>
                <Select multiple value={filters.subscriptionStatus} onChange={e => setFilter("subscriptionStatus", e.target.value)}
                  renderValue={s => <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>{s.map(v => <Chip key={v} label={v} size="small" />)}</Box>}>
                  {["free", "premium", "expired"].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </AccordionDetails>
          </Accordion>

          <Accordion><AccordionSummary expandIcon={<ExpandMore />}><Typography>Engagement</Typography></AccordionSummary>
            <AccordionDetails>
              <TextField label="Last Active (days)" type="number" fullWidth size="small" value={filters.lastActiveWithin || ""}
                onChange={e => setFilter("lastActiveWithin", e.target.value ? parseInt(e.target.value) : null)} margin="dense" />
              {[["profileCompleted", "Profile Completed"], ["hasPhoto", "Has Photo"], ["hasCV", "Has CV"],
                ["hasExpoToken", "Has Push Token"], ["hasMobile", "Has Mobile"]].map(([k, l]) => (
                <FormControlLabel key={k} control={<Checkbox size="small" checked={filters[k]} onChange={e => setFilter(k, e.target.checked)} />} label={l} />
              ))}
            </AccordionDetails>
          </Accordion>

          <Accordion><AccordionSummary expandIcon={<ExpandMore />}><Typography>Exclude</Typography></AccordionSummary>
            <AccordionDetails>
              {[["skipInactive", "Skip Inactive"], ["skipDisabled", "Skip Blocked"], ["skipWithoutCV", "Skip Without CV"]].map(([k, l]) => (
                <FormControlLabel key={k} control={<Checkbox size="small" checked={filters[k]} onChange={e => setFilter(k, e.target.checked)} />} label={l} />
              ))}
            </AccordionDetails>
          </Accordion>
        </Paper>
      )}

      {/* Selected Users */}
      {selectedUsers.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Selected ({selectedUserIds.length})</Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {selectedUsers.map(u => (
              <Chip key={u.id} label={`${u.name} (${u.mobile})`} onDelete={() => onRemoveUser?.(u.id)}
                avatar={<Avatar src={u.photo}><Person /></Avatar>} variant="outlined" color="primary" />
            ))}
          </Box>
        </Box>
      )}

      {/* User List */}
      <Paper sx={{ border: "1px solid", borderColor: "divider" }}>
        <Box sx={{ p: 1.5, bgcolor: "grey.100", borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle2" color="text.secondary">
            {search.length >= 2 ? `Results (${pagination.total})` : `All Users (${pagination.total})`}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}><CircularProgress size={24} /></Box>
        ) : users.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}><Typography color="text.secondary">No users found</Typography></Box>
        ) : (
          <>
            <List ref={listRef} sx={{ maxHeight: 350, overflow: "auto" }}>
              {users.map(u => (
                <ListItem key={u.id} disablePadding>
                  <ListItemButton onClick={() => toggle(u)}>
                    <Checkbox checked={selectedUserIds.includes(u.id)} />
                    <ListItemAvatar><Avatar src={u.photo}><Person /></Avatar></ListItemAvatar>
                    <ListItemText primary={u.name} secondary={`${u.mobile} • ${u.email || "No email"}`} />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            {pagination.totalPages > 1 && (
              <Box sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", textAlign: "center" }}>
                <Pagination count={pagination.totalPages} page={pagination.page} onChange={handlePage} color="primary" size="small" />
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default UserSelector;
